/**
 * printQueueSheet — A4 print renderer for the Manage Visit queue print sheet
 * (TOKEN-UPDATE/T2, FE-B).
 *
 * Uses the same hidden-iframe + window.print() lifecycle as printToken.js
 * (80mm token printer), sized for A4 instead. WYSIWYG of the caller's
 * (already dept/provider/status/queue-sorted) `filteredVisits` array — one
 * row per visit, no re-sorting or re-filtering here.
 *
 * OPD columns: Queue No. · Patient · UHID · Net Paid · Receipt Id.
 * LAB columns: Queue No. · Patient · UHID · Test / Investigation ·
 *              Advised by · Technician · Net Paid · Receipt Id.
 *
 * Net Paid / Test-Investigation are read from `summaryMap` (the
 * `print-summary` response, keyed by visit_id) — a visit_id absent from the
 * map renders a blank Net cell plus a page footnote ("— amount unavailable"),
 * never a crash / never an unfiltered fallback.
 *
 * Issue-2 (refunds on queue sheets): `summaryMap[visit_id]` additionally
 * carries `net_rupees` (gross − refunded), `refunded_rupees`, `cancelled`,
 * and `refunds: [{ receipt_number, amount_rupees }]` (FROZEN contract,
 * build-plan.md §1). A pre-upgrade summary entry (old shape — only
 * `amount_collected_rupees`/`lab_tests`) degrades gracefully: the Net Paid
 * column falls back to `amount_collected_rupees`, no refund note, no
 * CANCELLED badge — renders exactly as it did before Issue-2, never NaN.
 *
 * Day Summary footer (Collected/Refunded/Net) is summed HERE, over the
 * PRINTED rows only (`rows` × `summaryMap`) — never the response-level
 * `day_totals` (that covers the caller's whole server-scoped day, which
 * would not tie to a client-filtered subset of rows). See build-plan.md
 * "Architect contract check" for the reasoning.
 *
 * NO network call happens inside this util (RC10 — the caller fetches
 * print-summary exactly once and passes the result in as `summaryMap`).
 *
 * Frozen signature (t2-build-plan.md):
 *   printQueueSheet({ title, subtitle, provider, rows, summaryMap })
 *
 * TOKEN-UPDATE/T4 also exports `printThermalQueueSheet` (below) — an 80mm
 * sibling renderer for the same `filteredVisits`/`summaryMap` data, reusing
 * printToken.js's 80mm/legibility conventions (72mm body, solid #000, Arial,
 * no pagination). Kept in this module (not a new file) so ONE module owns
 * queue-sheet rendering; the A4 path above is untouched.
 *
 * Issue-3 (section the "All" print by queue): both renderers accept an
 * optional `sectioned` flag. When `sectioned` is truthy AND
 * `groupVisitsForPrint(rows, summaryMap)` yields ONE OR MORE top-level groups, a
 * per-group render runs instead (new `renderSectionedA4` /
 * `renderSectionedThermal` below) — one top-level `.section-hdr` heading
 * per queue category (OPD → OTC → Lab → Radiology, LAB columns only in the
 * Lab and Radiology groups),
 * WYSIWYG of `groupVisitsForPrint`'s group order. The Grand Day Summary
 * footer stays a SINGLE sum over every printed row across all
 * groups/sections (no per-group/per-section subtotals — owner ruling).
 *
 * F5 (owner ruling, hierarchical section headers — bugfix-jul31 scope.md
 * F5): the OPD group nests a lighter `.subsection-hdr` per doctor (plus a
 * trailing "Unassigned" subsection) INSIDE the single top-level "OPD"
 * heading, instead of each doctor rendering as its own bare top-level
 * header. OTC/Lab stay flat single-group sections (no sub-header) exactly
 * as before. `.subsection-hdr` is a lighter-but-clear variant of
 * `.section-hdr` (A4: smaller heading, no border-bottom rule; thermal:
 * bold name line without the top-level's border-bottom rule) — same
 * solid-#000 thermal constraint, no grey.
 *
 * F3 (owner live-test ruling, 2026-08-02): a `sectioned` print with only
 * ONE queue present (e.g. a day of only OTC visits) still renders the
 * sectioned layout — same `.section-hdr` heading as a multi-category day,
 * not the flat headerless table. `sectioned` only ever collapses to the
 * flat body when `rows` produces ZERO groups (the empty-set case) or
 * when the caller passes `sectioned: false` (a specific provider/nurse/lab
 * filter print) — both stay byte-identical to the pre-Issue-3 output.
 *
 * lab-settle-daybucket (FE-1, binds C1/C2 — build-plan.md): an IPD-lab row's
 * `summaryMap[visit_id]` (visit_id = lab_order_id for these rows) carries
 * `settlement_status: 'SETTLED' | 'UNSETTLED'`. SETTLED renders its
 * `net_rupees` exactly like any other row. UNSETTLED renders the
 * UNSETTLED_FLAG_TEXT chip in the Net Paid cell INSTEAD of the amount
 * (never a bare ₹0 / blank) and is excluded from the Day Summary footer
 * (Collected/Refunded/Net) — it's a receivable memo, not collected money.
 * A row with no `settlement_status` on its summary (every OPD row, and any
 * pre-upgrade summary) is untouched by this branch. The Lab group itself is
 * split IPD/OPD by `groupVisitsForPrint` (see that file) — this module only
 * owns the per-row amount cell and the footer sum.
 *
 * BUSY-VOUCHER R1 (OR-41/OR-48/OR-55): `groupVisitsForPrint(rows, summaryMap)`
 * replaces the single "Lab Visits" group with "Lab" then "Radiology" groups
 * (section decided by the server, `summaryMap[id].lab_sections`). A visit
 * with tests in both sections is emitted once per section as
 * `{ ...visit, _printSlice }`; EVERY row-level read goes through
 * `rowSummary(summaryMap, visit)`, which overlays that slice (its own tests,
 * amount, refunds) on the visit's summary. The Day Summary and the "amount
 * unavailable" footnote read the ORIGINAL (un-split) `visitRows`, whose
 * `_printSlice` is absent — so the printed total is unchanged by the split
 * (the server guarantees Σ slices == the visit's top-level money). Lab and
 * Radiology rows carry "Advised by": an A4 column (after Test /
 * Investigation) and, on thermal, an "Advised by: {name|SELF}" line under
 * the patient name — both from the shared `advisedByName` (never re-derived).
 */
import { resolveUhid } from './uhid';
import { escapeHtml } from './escapeHtml';
import { groupVisitsForPrint, LAB_GROUP_KINDS } from './groupVisitsForPrint';
import { advisedByName } from '../components/visit/advisedByAttribution';

const AMOUNT_UNAVAILABLE_FOOTNOTE = '— amount unavailable';
// COPY: pending Business ratification (build-plan.md — owner used "UNSETTLED
// on payment"; PO to confirm final wording/tooltip).
const UNSETTLED_FLAG_TEXT = 'UNSETTLED';

const OPD_COLUMNS = ['Queue No.', 'Patient', 'UHID', 'Net Paid', 'Receipt Id'];
const LAB_COLUMNS = [
  'Queue No.', 'Patient', 'UHID', 'Test / Investigation', 'Advised by', 'Technician', 'Net Paid', 'Receipt Id',
];
// Walk-in (no advising doctor) — printed bold so it stands out (mockup).
const SELF_TEXT = 'SELF';

function formatAmount(value) {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return `₹${n.toLocaleString('en-IN')}`;
}

// Finite-number-or-null — the single coercion every rupee value (row cell,
// footer sum, "No refund recorded" gate) routes through, so a bad/missing
// value degrades to null (never NaN, never silently 0 where a fallback
// applies) consistently everywhere it's read.
function numOrNull(value) {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

// Net Paid value for a visit: `net_rupees` (post Issue-2 backend) with a
// fallback to the pre-upgrade `amount_collected_rupees` — the ONE place
// this fallback is decided, so the row cell and the footer sum can never
// disagree on what a given visit's "amount printed" is.
function netFor(summary) {
  if (!summary) return null;
  const net = numOrNull(summary.net_rupees);
  if (net !== null) return net;
  return numOrNull(summary.amount_collected_rupees);
}

// The summary every row-level read uses. A Lab/Radiology row emitted per
// section (`_printSlice`, see groupVisitsForPrint) overlays its slice — own
// tests, amount, refunds — on the visit's summary; any other row reads its
// summary entry as-is (so the un-split `visitRows` the Day Summary sums are
// untouched).
function rowSummary(summaryMap, visit) {
  const summary = (summaryMap && summaryMap[visit?.visit_id]) || undefined;
  return visit?._printSlice ? { ...summary, ...visit._printSlice } : summary;
}

// "Advised by" display: the shared bare doctor name, else SELF.
function advisedByText(visit) {
  return advisedByName(visit) || SELF_TEXT;
}

// One refund note PER refunds[i] entry (never the aggregate `refunded_rupees`
// paired with multiple receipt ids) — architect contract-check clarification
// (a). `receipt_number: null` omits the "· Rx" segment (clarification (b)).
// `receipt_series` (W7) labels a credit-note refund "CN <n>" instead of
// "Rx <n>" — same convention as printToken.js / VisitReceiptsModal — and
// degrades to "Rx" when the field is absent (pre-upgrade summary rows).
function refundNoteText(refund) {
  const amount = formatAmount(refund?.amount_rupees);
  if (amount === null) return null;
  const receiptNumber = refund?.receipt_number;
  const label = refund?.receipt_series === 'CN' ? 'CN' : 'Rx';
  const rxSuffix = (receiptNumber === null || receiptNumber === undefined)
    ? ''
    : ` · ${label} ${escapeHtml(String(receiptNumber))}`;
  return `Refund ${escapeHtml(amount)}${rxSuffix}`;
}

function refundsFor(summary) {
  return Array.isArray(summary?.refunds) ? summary.refunds : [];
}

function isUnsettled(summary) {
  return summary?.settlement_status === 'UNSETTLED';
}

// Footer totals = Σ over the PRINTED rows only, reading the same per-visit
// fields (and the same `netFor` fallback) the row cells render — ties to
// the Net Paid column by construction, under any client-side filter. An
// UNSETTLED IPD-lab row contributes ZERO regardless of what its summary
// carries — it's a receivable memo (rendered as the UNSETTLED flag, not an
// amount), never collected money, so it's excluded before the sum, not
// relied on to already be zero server-side.
function computeDayTotals(rows, summaryMap) {
  return rows.reduce((acc, visit) => {
    const summary = rowSummary(summaryMap, visit);
    if (isUnsettled(summary)) return acc;
    const collected = numOrNull(summary?.amount_collected_rupees) ?? 0;
    const refunded = numOrNull(summary?.refunded_rupees) ?? 0;
    const net = netFor(summary) ?? 0;
    return {
      collected: acc.collected + collected,
      refunded: acc.refunded + refunded,
      net: acc.net + net,
    };
  }, { collected: 0, refunded: 0, net: 0 });
}

// An UNSETTLED row already renders an explicit flag (never blank) — it's a
// known state, not a missing join, so it never trips the "amount
// unavailable" footnote even if its summary is otherwise sparse.
function hasMissingAmount(rows, summaryMap) {
  return rows.some((visit) => {
    const summary = rowSummary(summaryMap, visit);
    if (isUnsettled(summary)) return false;
    return netFor(summary) === null;
  });
}

// Shared iframe print lifecycle — identical behaviour to the inline blocks
// this replaces at the tail of `printQueueSheet` / `printThermalQueueSheet`
// / the two Issue-3 sectioned renderers below; extracted only to avoid
// four copies of the same DOM plumbing (the generated `html` string is
// what every test asserts against, not this wiring).
function printHtmlViaIframe(html) {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;';
  document.body.appendChild(iframe);

  iframe.contentDocument.open();
  iframe.contentDocument.write(html);
  iframe.contentDocument.close();

  let printed = false;
  const doPrint = () => {
    if (printed) return;
    printed = true;
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => {
      if (document.body.contains(iframe)) document.body.removeChild(iframe);
    }, 1000);
  };

  iframe.onload = () => doPrint();
  setTimeout(() => doPrint(), 500);
}

// ── A4 ───────────────────────────────────────────────────────────────────

function buildPatientCellHtml(visit, summary) {
  const patientCell = escapeHtml(visit?.patient_name || '—');
  const cancelled = summary?.cancelled === true;
  const badgeHtml = cancelled ? '<span class="tag-badge">CANCELLED</span>' : '';

  const refundNotesHtml = refundsFor(summary)
    .map((r) => {
      const text = refundNoteText(r);
      return text === null ? '' : `<span class="refund-note">${text}</span>`;
    })
    .join('');

  const net = netFor(summary);
  const noRefundNoteHtml = (cancelled && net !== null && net > 0)
    ? '<span class="status-note">No refund recorded</span>'
    : '';

  return `<span class="patient-name">${patientCell}</span>${badgeHtml}${refundNotesHtml}${noRefundNoteHtml}`;
}

function buildRowHtml(visit, summaryMap, isLab) {
  const summary = rowSummary(summaryMap, visit);
  const formattedAmount = formatAmount(netFor(summary));
  const amountCell = isUnsettled(summary)
    ? `<span class="unsettled-flag">${escapeHtml(UNSETTLED_FLAG_TEXT)}</span>`
    : (formattedAmount === null ? '' : escapeHtml(formattedAmount));

  const receiptCell = escapeHtml(visit?.receipt_number || '—');
  const patientCellHtml = buildPatientCellHtml(visit, summary);
  // GST-DOCNO/R-GD-6, T-UHID-BUG (2026-08-26) + D36 (owner, same day) — the
  // queue sheet's UHID column carries money too (Net Paid, Receipt Id, both
  // OPD and Lab), so it now prints the FULL real UHID — the SAME
  // `resolveUhid()` every other money document uses, not a separate
  // short-form helper. A patient with no minted UHID (D35) still gets the
  // short `deriveUhid(patient_id)` fallback — the column legitimately mixes
  // both forms; that is intended, not a bug.
  const uhidCell = escapeHtml(resolveUhid({ uhid: visit?.patient_uhid, patient_id: visit?.patient_id }));
  const queueCell = escapeHtml(
    visit?.queue_number !== null && visit?.queue_number !== undefined ? String(visit.queue_number) : '—',
  );

  if (!isLab) {
    return `<tr>
      <td>${queueCell}</td>
      <td>${patientCellHtml}</td>
      <td>${uhidCell}</td>
      <td class="num">${amountCell}</td>
      <td>${receiptCell}</td>
    </tr>`;
  }

  const labTests = Array.isArray(summary?.lab_tests) ? summary.lab_tests : [];
  // R1: a per-section slice with no tests (e.g. a registration fee on a
  // Lab & Diagnostic visit, which the server books to the Lab slice) prints
  // the same dash as an empty Technician cell instead of a blank cell.
  const testsCell = (visit?._printSlice && labTests.length === 0)
    ? '—'
    : escapeHtml(labTests.join(', '));
  const technicianCell = escapeHtml(visit?.lab_technician_name || '—');
  const advisedBy = advisedByText(visit);
  const advisedByCell = `<td${advisedBy === SELF_TEXT ? ' class="selfc"' : ''}>${escapeHtml(advisedBy)}</td>`;

  return `<tr>
    <td>${queueCell}</td>
    <td>${patientCellHtml}</td>
    <td>${uhidCell}</td>
    <td>${testsCell}</td>
    ${advisedByCell}
    <td>${technicianCell}</td>
    <td class="num">${amountCell}</td>
    <td>${receiptCell}</td>
  </tr>`;
}

// ── Issue-3/F5 — sectioned A4 render (OPD{Doctors → Unassigned} → OTC → Lab) ─
//
// One top-level `.section-hdr` heading per GROUP (OPD / OTC / Lab), and
// inside it one `<table>` per SECTION — a doctor/Unassigned subsection
// inside OPD gets its own lighter `.subsection-hdr` before its table; the
// single OTC/Lab section has no subsection title (flat, as before) so no
// `.subsection-hdr` renders. Column set (OPD vs LAB) is decided once per
// GROUP (`group.kind === 'LAB'`). All inside the SAME `<html>/<head>/<style>`
// shell + `.sheet-hdr` header as the flat A4 sheet — a standalone copy of
// that shell (not a shared constant) so the flat path above is never
// touched by this addition. The Grand Day Summary footer sums every
// PRINTED row across ALL groups/sections (no per-group/per-section
// subtotals — owner ruling).
function buildSectionTableHtml(section, columns, theadCellsHtml, summaryMap, isLab) {
  const subHeaderHtml = section.title ? `<h3 class="subsection-hdr">${escapeHtml(section.title)}</h3>` : '';
  const bodyRowsHtml = section.rows.map((visit) => buildRowHtml(visit, summaryMap, isLab)).join('');

  return `${subHeaderHtml}<table>
    <thead><tr>${theadCellsHtml}</tr></thead>
    <tbody>${bodyRowsHtml}</tbody>
  </table>`;
}

function buildGroupHtml(group, summaryMap) {
  const isLab = LAB_GROUP_KINDS.includes(group.kind);
  const columns = isLab ? LAB_COLUMNS : OPD_COLUMNS;
  const theadCellsHtml = columns
    .map((label) => `<th${label === 'Net Paid' ? ' class="num"' : ''}>${escapeHtml(label)}</th>`)
    .join('');
  const sectionsHtml = group.sections
    .map((section) => buildSectionTableHtml(section, columns, theadCellsHtml, summaryMap, isLab))
    .join('');

  return `<h2 class="section-hdr">${escapeHtml(group.title)}</h2>${sectionsHtml}`;
}

function renderSectionedA4({
  title, subtitle, groups, summaryMap, visitRows,
}) {
  const sectionsHtml = groups.map((group) => buildGroupHtml(group, summaryMap)).join('');

  const totals = computeDayTotals(visitRows, summaryMap);
  const footerHtml = `<p class="footer-line">Day Summary — Collected ${escapeHtml(formatAmount(totals.collected))} `
    + `· Refunded ${escapeHtml(formatAmount(totals.refunded))} · Net ${escapeHtml(formatAmount(totals.net))}</p>`;

  const footnoteHtml = hasMissingAmount(visitRows, summaryMap)
    ? `<p class="footnote">${escapeHtml(AMOUNT_UNAVAILABLE_FOOTNOTE)}</p>`
    : '';

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
  @page { size: A4; margin: 14mm 12mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #111827; background: #fff; }
  .sheet-hdr { text-align: center; border-bottom: 2px solid #111827; padding-bottom: 10px; margin-bottom: 14px; }
  .org-name { font-size: 16px; font-weight: 800; }
  .subtitle { font-size: 11px; color: #334155; margin-top: 4px; }
  table { width: 100%; border-collapse: collapse; font-size: 10px; margin-bottom: 10px; }
  thead { display: table-header-group; }
  tr { page-break-inside: avoid; }
  th, td { border: 1px solid #cbd5e1; padding: 5px 6px; text-align: left; }
  th { background: #f8fafc; font-weight: 700; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
  td.selfc { font-weight: 700; }
  .section-hdr {
    font-size: 12px; font-weight: 800; color: #111827; margin: 12px 0 6px;
    border-bottom: 1px solid #cbd5e1; padding-bottom: 3px;
  }
  .subsection-hdr {
    font-size: 10px; font-weight: 700; color: #334155; margin: 8px 0 4px;
  }
  .footer-line { margin-top: 14px; font-size: 11px; font-weight: 700; }
  .footnote { margin-top: 6px; font-size: 9px; color: #64748b; font-style: italic; }
  .patient-name { font-weight: 400; }
  .refund-note { display: block; font-size: 8px; color: #64748b; font-style: italic; margin-top: 2px; }
  .status-note { display: block; font-size: 8px; color: #64748b; font-style: italic; margin-top: 2px; }
  .tag-badge {
    display: inline-block; border: 1px solid #111827; border-radius: 3px;
    padding: 0 4px; margin-left: 6px; font-size: 8px; font-weight: 800;
    letter-spacing: 0.5px; text-transform: uppercase; vertical-align: middle;
  }
  .unsettled-flag {
    display: inline-block; border: 1px solid #f59e0b; border-radius: 3px;
    background: #fef3c7; color: #92400e; padding: 1px 5px; font-size: 8px;
    font-weight: 800; letter-spacing: 0.5px; text-transform: uppercase;
    white-space: nowrap;
  }
</style>
</head>
<body>
  <div class="sheet-hdr">
    <p class="org-name">${escapeHtml(title || '')}</p>
    ${subtitle ? `<p class="subtitle">${escapeHtml(subtitle)}</p>` : ''}
  </div>
  ${sectionsHtml}
  ${footerHtml}
  ${footnoteHtml}
</body>
</html>`;

  printHtmlViaIframe(html);
}

export function printQueueSheet({
  title, subtitle, provider, rows, summaryMap, sectioned,
} = {}) {
  const isLab = provider === 'LAB';
  const visitRows = Array.isArray(rows) ? rows : [];

  if (sectioned) {
    const groups = groupVisitsForPrint(visitRows, summaryMap);
    // F3: render the sectioned layout for ANY non-empty group set (even a
    // single queue) — only zero groups falls through to the flat body.
    if (groups.length > 0) {
      renderSectionedA4({
        title, subtitle, groups, summaryMap, visitRows,
      });
      return;
    }
  }

  const columns = isLab ? LAB_COLUMNS : OPD_COLUMNS;

  const theadCellsHtml = columns
    .map((label) => `<th${label === 'Net Paid' ? ' class="num"' : ''}>${escapeHtml(label)}</th>`)
    .join('');

  const bodyRowsHtml = visitRows.map((visit) => buildRowHtml(visit, summaryMap, isLab)).join('');

  const totals = computeDayTotals(visitRows, summaryMap);
  const footerHtml = `<p class="footer-line">Day Summary — Collected ${escapeHtml(formatAmount(totals.collected))} `
    + `· Refunded ${escapeHtml(formatAmount(totals.refunded))} · Net ${escapeHtml(formatAmount(totals.net))}</p>`;

  const footnoteHtml = hasMissingAmount(visitRows, summaryMap)
    ? `<p class="footnote">${escapeHtml(AMOUNT_UNAVAILABLE_FOOTNOTE)}</p>`
    : '';

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
  @page { size: A4; margin: 14mm 12mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #111827; background: #fff; }
  .sheet-hdr { text-align: center; border-bottom: 2px solid #111827; padding-bottom: 10px; margin-bottom: 14px; }
  .org-name { font-size: 16px; font-weight: 800; }
  .subtitle { font-size: 11px; color: #334155; margin-top: 4px; }
  table { width: 100%; border-collapse: collapse; font-size: 10px; }
  thead { display: table-header-group; }
  tr { page-break-inside: avoid; }
  th, td { border: 1px solid #cbd5e1; padding: 5px 6px; text-align: left; }
  th { background: #f8fafc; font-weight: 700; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
  .footer-line { margin-top: 14px; font-size: 11px; font-weight: 700; }
  .footnote { margin-top: 6px; font-size: 9px; color: #64748b; font-style: italic; }
  .patient-name { font-weight: 400; }
  .refund-note { display: block; font-size: 8px; color: #64748b; font-style: italic; margin-top: 2px; }
  .status-note { display: block; font-size: 8px; color: #64748b; font-style: italic; margin-top: 2px; }
  .tag-badge {
    display: inline-block; border: 1px solid #111827; border-radius: 3px;
    padding: 0 4px; margin-left: 6px; font-size: 8px; font-weight: 800;
    letter-spacing: 0.5px; text-transform: uppercase; vertical-align: middle;
  }
  .unsettled-flag {
    display: inline-block; border: 1px solid #f59e0b; border-radius: 3px;
    background: #fef3c7; color: #92400e; padding: 1px 5px; font-size: 8px;
    font-weight: 800; letter-spacing: 0.5px; text-transform: uppercase;
    white-space: nowrap;
  }
</style>
</head>
<body>
  <div class="sheet-hdr">
    <p class="org-name">${escapeHtml(title || '')}</p>
    ${subtitle ? `<p class="subtitle">${escapeHtml(subtitle)}</p>` : ''}
  </div>
  <table>
    <thead><tr>${theadCellsHtml}</tr></thead>
    <tbody>${bodyRowsHtml}</tbody>
  </table>
  ${footerHtml}
  ${footnoteHtml}
</body>
</html>`;

  printHtmlViaIframe(html);
}

// ── T4 — Thermal (80mm) queue print ─────────────────────────────────────
//
// v4-alt rebuild (Issue-2, owner-approved mockup 2-thermal-v4-alt): one flex
// row per visit — queue no + name (flex:1, wraps under itself) + hero Net
// amount, all sharing the row's first line (`align-items: flex-start` keeps
// the amount pinned to the top even when the name wraps). A cancelled visit
// becomes an inverse (#000 fill / #fff text) full-width name-row — there is
// no separate banner element to misattribute to the row above/below.
// Refund/status sub-notes render right-aligned under the amount. Plain
// stacked flow throughout — no negative margins, no absolute positioning.
function buildThermalPatientBlockHtml(visit, summaryMap, isLab = false) {
  const summary = rowSummary(summaryMap, visit);
  const cancelled = summary?.cancelled === true;
  const net = netFor(summary);
  const formattedNet = formatAmount(net);
  const amountCell = isUnsettled(summary)
    ? `<span class="unsettled-flag">${escapeHtml(UNSETTLED_FLAG_TEXT)}</span>`
    : (formattedNet === null ? '' : escapeHtml(formattedNet));
  const patientCell = escapeHtml(visit?.patient_name || '—');
  const queueCell = escapeHtml(
    visit?.queue_number !== null && visit?.queue_number !== undefined ? String(visit.queue_number) : '—',
  );

  const lineTopHtml = `<div class="line-top">
    <span class="qno">${queueCell}</span><span class="pname">${patientCell}</span>${
      cancelled ? '<span class="status-word">CANCELLED</span>' : ''
    }<span class="line-amt">${amountCell}</span>
  </div>`;

  const nameRowHtml = cancelled ? `<div class="name-bar">${lineTopHtml}</div>` : lineTopHtml;

  // OR-48/OR-55: Lab and Radiology group rows carry one small
  // "Advised by: {name|SELF}" line under the patient name (sectioned print
  // only — the flat thermal body has no Lab concept and stays untouched).
  let advisedLineHtml = '';
  if (isLab) {
    const advisedBy = advisedByText(visit);
    advisedLineHtml = `<div class="ref-line${advisedBy === SELF_TEXT ? ' self' : ''}">Advised by: ${escapeHtml(advisedBy)}</div>`;
  }

  const refunds = refundsFor(summary);
  const formattedGross = formatAmount(summary?.amount_collected_rupees);
  const grossNoteHtml = (refunds.length > 0 && formattedGross !== null)
    ? `<div class="gross-note">Amount ${escapeHtml(formattedGross)}</div>`
    : '';
  const refundNotesHtml = refunds
    .map((r) => {
      const text = refundNoteText(r);
      return text === null ? '' : `<div class="refund-note">${text}</div>`;
    })
    .join('');
  const statusNoteHtml = (cancelled && net !== null && net > 0)
    ? '<div class="status-note">No refund recorded</div>'
    : '';

  return `<div class="patient-block${cancelled ? ' cancelled' : ''}">
    ${nameRowHtml}${advisedLineHtml}
    ${grossNoteHtml}
    ${refundNotesHtml}
    ${statusNoteHtml}
  </div>`;
}

// ── Issue-3/F5 — sectioned thermal render ────────────────────────────────
//
// Same 72mm shell + `.slip-header` + summary/footnote blocks as the flat
// thermal sheet (standalone copy — the flat function below is never
// touched by this addition). Blocks are category-agnostic on thermal (no
// column concept), so a GROUP is a bold `.section-hdr` divider line, and
// (OPD only) each doctor/Unassigned SECTION inside it gets a lighter
// `.subsection-hdr` name line (no border-bottom rule) before its blocks —
// OTC/Lab have a single untitled section (flat, as before, no
// `.subsection-hdr`). All inside the SAME `.rows` container — one summary
// block at the bottom, over ALL rows.
function buildThermalSectionHtml(section, summaryMap, isLab) {
  const subHeaderHtml = section.title ? `<div class="subsection-hdr">${escapeHtml(section.title)}</div>` : '';
  const blocksHtml = section.rows.map((visit) => buildThermalPatientBlockHtml(visit, summaryMap, isLab)).join('');
  return `${subHeaderHtml}${blocksHtml}`;
}

function buildThermalGroupHtml(group, summaryMap) {
  const isLab = LAB_GROUP_KINDS.includes(group.kind);
  const sectionsHtml = group.sections
    .map((section) => buildThermalSectionHtml(section, summaryMap, isLab))
    .join('');
  return `<div class="section-hdr">${escapeHtml(group.title)}</div>${sectionsHtml}`;
}

function renderSectionedThermal({
  orgName, filterLine, dateLabel, groups, summaryMap, visitRows,
}) {
  const rowsHtml = groups.map((group) => buildThermalGroupHtml(group, summaryMap)).join('');

  const totals = computeDayTotals(visitRows, summaryMap);
  const summaryBlockHtml = `<div class="summary-block">
    <div class="summary-row"><span>Collected</span><span>${escapeHtml(formatAmount(totals.collected))}</span></div>
    <div class="summary-row"><span>Refunded</span><span>${escapeHtml(formatAmount(totals.refunded))}</span></div>
    <div class="summary-row net"><span>Net</span><span>${escapeHtml(formatAmount(totals.net))}</span></div>
  </div>`;

  const footnoteHtml = hasMissingAmount(visitRows, summaryMap)
    ? `<p class="footnote">${escapeHtml(AMOUNT_UNAVAILABLE_FOOTNOTE)}</p>`
    : '';

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
  @page { size: 80mm auto; margin: 3mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    width: 72mm;
    font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif;
    font-size: 13px;
    color: #000;
    background: #fff;
  }
  .slip-header { text-align: center; padding-bottom: 1.5mm; border-bottom: 1px dashed #000; margin-bottom: 1.5mm; }
  .org-name { font-size: 16px; font-weight: 800; text-transform: uppercase; }
  .filter-line { font-size: 14px; font-weight: 700; margin-top: 0.5mm; }
  .date-line { font-size: 12px; font-weight: 600; margin-top: 0.5mm; }

  .section-hdr {
    font-weight: 800; font-size: 14px; color: #000; background: #fff;
    border-bottom: 1px solid #000; padding: 1.5mm 0 1mm; margin-top: 1mm;
    text-transform: uppercase; overflow-wrap: anywhere;
  }
  .section-hdr:first-child { margin-top: 0; }

  .subsection-hdr {
    font-weight: 800; font-size: 13px; color: #000; background: #fff;
    padding: 1mm 0 0.5mm; margin-top: 0.5mm; overflow-wrap: anywhere;
  }

  .patient-block { padding: 2.2mm 0; border-bottom: 1px dashed #000; }
  .patient-block:last-child { border-bottom: none; }
  .patient-block.cancelled { padding-bottom: 3mm; }

  .line-top { display: flex; align-items: flex-start; gap: 2mm; }
  .qno { font-size: 14px; font-weight: 800; flex: 0 0 auto; min-width: 6mm; }
  .pname { font-size: 13px; font-weight: 600; flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; }
  .line-amt { font-size: 19px; font-weight: 800; flex: 0 0 auto; white-space: nowrap; text-align: right; }

  .name-bar { background: #000; color: #fff; padding: 1.2mm 2mm; }
  .name-bar .line-top { align-items: flex-start; }
  .name-bar .qno, .name-bar .pname { color: #fff; }
  .name-bar .pname { font-weight: 700; }
  .name-bar .status-word {
    color: #fff; font-size: 11px; font-weight: 800; letter-spacing: 1px;
    flex: 0 0 auto; white-space: nowrap;
  }
  .name-bar .line-amt { color: #fff; }

  .ref-line { font-size: 10px; font-weight: 700; margin: 0.3mm 0 0 8mm; overflow-wrap: anywhere; }
  .ref-line.self { letter-spacing: 0.3px; }
  .gross-note { text-align: right; font-size: 10px; color: #000; margin-top: 0.3mm; }
  .refund-note { text-align: right; font-size: 10px; font-style: italic; font-weight: 700; margin-top: 0.3mm; }
  .status-note { text-align: right; font-size: 10px; font-style: italic; margin-top: 0.4mm; }
  .unsettled-flag { font-weight: 800; letter-spacing: 0.5px; text-transform: uppercase; }

  .summary-block { margin-top: 1.5mm; padding-top: 1.5mm; border-top: 1px dashed #000; }
  .summary-row { display: flex; justify-content: space-between; font-size: 13px; font-weight: 700; padding: 0.4mm 0; }
  .summary-row.net { font-size: 16px; font-weight: 800; border-top: 1px solid #000; margin-top: 0.5mm; padding-top: 1mm; }

  .footnote { margin-top: 1mm; font-size: 11px; color: #000; font-style: italic; text-align: center; }
</style>
</head>
<body>
  <div class="slip-header">
    <div class="org-name">${escapeHtml(orgName || '')}</div>
    ${filterLine ? `<div class="filter-line">${escapeHtml(filterLine)}</div>` : ''}
    <div class="date-line">${escapeHtml(dateLabel || '')}</div>
  </div>
  <div class="rows">${rowsHtml}</div>
  ${summaryBlockHtml}
  ${footnoteHtml}
</body>
</html>`;

  printHtmlViaIframe(html);
}

/**
 * printThermalQueueSheet({ orgName, filterLine, dateLabel, rows, summaryMap, sectioned })
 *
 * `filterLine` — the SAME `filterBits.join(' · ')` string the caller already
 * composes for the A4 subtitle (AC2); pass '' when there are no active
 * filters (e.g. scoped DOCTOR self-print) and the line is simply omitted.
 *
 * `sectioned` — Issue-3/F5: when truthy AND `groupVisitsForPrint(rows, summaryMap)`
 * yields one or more top-level groups (F3: even a single queue), renders
 * per-group blocks with a bold `.section-hdr` divider — OPD nesting a
 * lighter `.subsection-hdr` per doctor/Unassigned (see
 * `renderSectionedThermal` above) — instead of the flat body below. Only a
 * ZERO-group result (empty rows) falls through to the flat body.
 */
export function printThermalQueueSheet({
  orgName, filterLine, dateLabel, rows, summaryMap, sectioned,
} = {}) {
  const visitRows = Array.isArray(rows) ? rows : [];

  if (sectioned) {
    const groups = groupVisitsForPrint(visitRows, summaryMap);
    if (groups.length > 0) {
      renderSectionedThermal({
        orgName, filterLine, dateLabel, groups, summaryMap, visitRows,
      });
      return;
    }
  }

  const rowsHtml = visitRows.map((visit) => buildThermalPatientBlockHtml(visit, summaryMap)).join('');

  const totals = computeDayTotals(visitRows, summaryMap);
  const summaryBlockHtml = `<div class="summary-block">
    <div class="summary-row"><span>Collected</span><span>${escapeHtml(formatAmount(totals.collected))}</span></div>
    <div class="summary-row"><span>Refunded</span><span>${escapeHtml(formatAmount(totals.refunded))}</span></div>
    <div class="summary-row net"><span>Net</span><span>${escapeHtml(formatAmount(totals.net))}</span></div>
  </div>`;

  const footnoteHtml = hasMissingAmount(visitRows, summaryMap)
    ? `<p class="footnote">${escapeHtml(AMOUNT_UNAVAILABLE_FOOTNOTE)}</p>`
    : '';

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
  @page { size: 80mm auto; margin: 3mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    width: 72mm;
    font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif;
    font-size: 13px;
    color: #000;
    background: #fff;
  }
  .slip-header { text-align: center; padding-bottom: 1.5mm; border-bottom: 1px dashed #000; margin-bottom: 1.5mm; }
  .org-name { font-size: 16px; font-weight: 800; text-transform: uppercase; }
  .filter-line { font-size: 14px; font-weight: 700; margin-top: 0.5mm; }
  .date-line { font-size: 12px; font-weight: 600; margin-top: 0.5mm; }

  .patient-block { padding: 2.2mm 0; border-bottom: 1px dashed #000; }
  .patient-block:last-child { border-bottom: none; }
  .patient-block.cancelled { padding-bottom: 3mm; }

  .line-top { display: flex; align-items: flex-start; gap: 2mm; }
  .qno { font-size: 14px; font-weight: 800; flex: 0 0 auto; min-width: 6mm; }
  .pname { font-size: 13px; font-weight: 600; flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; }
  .line-amt { font-size: 19px; font-weight: 800; flex: 0 0 auto; white-space: nowrap; text-align: right; }

  .name-bar { background: #000; color: #fff; padding: 1.2mm 2mm; }
  .name-bar .line-top { align-items: flex-start; }
  .name-bar .qno, .name-bar .pname { color: #fff; }
  .name-bar .pname { font-weight: 700; }
  .name-bar .status-word {
    color: #fff; font-size: 11px; font-weight: 800; letter-spacing: 1px;
    flex: 0 0 auto; white-space: nowrap;
  }
  .name-bar .line-amt { color: #fff; }

  .gross-note { text-align: right; font-size: 10px; color: #000; margin-top: 0.3mm; }
  .refund-note { text-align: right; font-size: 10px; font-style: italic; font-weight: 700; margin-top: 0.3mm; }
  .status-note { text-align: right; font-size: 10px; font-style: italic; margin-top: 0.4mm; }
  .unsettled-flag { font-weight: 800; letter-spacing: 0.5px; text-transform: uppercase; }

  .summary-block { margin-top: 1.5mm; padding-top: 1.5mm; border-top: 1px dashed #000; }
  .summary-row { display: flex; justify-content: space-between; font-size: 13px; font-weight: 700; padding: 0.4mm 0; }
  .summary-row.net { font-size: 16px; font-weight: 800; border-top: 1px solid #000; margin-top: 0.5mm; padding-top: 1mm; }

  .footnote { margin-top: 1mm; font-size: 11px; color: #000; font-style: italic; text-align: center; }
</style>
</head>
<body>
  <div class="slip-header">
    <div class="org-name">${escapeHtml(orgName || '')}</div>
    ${filterLine ? `<div class="filter-line">${escapeHtml(filterLine)}</div>` : ''}
    <div class="date-line">${escapeHtml(dateLabel || '')}</div>
  </div>
  <div class="rows">${rowsHtml}</div>
  ${summaryBlockHtml}
  ${footnoteHtml}
</body>
</html>`;

  printHtmlViaIframe(html);
}
